// Loot rarity and item pools per zone (CHANGELOG 0.2.0, section 4). All numbers
// are data: change the tables here, the logic below reads them. Bosses, hordes,
// daily crates and crafting have their own tables and do not use these.
//
//   weights[R,E,L] = base[zone][r] × min(levelMult × luckMult, 1.5)
//   weights[L]     = min(weights[L] + pity, 1.0)
//   weights[U]     = base[zone][U]
//   weights[C]     = 100 − (U + R + E + L)

import { ITEM_TEMPLATES, isSlotLocked, type ItemTemplate } from '../content/items';
import { ITEM_PACKAGE_IDS } from './mvpItems';
import { ZONES } from '../content/zones';
import type { Rng } from '../rng';
import { RARITIES, type GearSlot, type Rarity, type SetId, type ZoneId } from '../types';

/** Chance per drop in percent; each row adds up to 100. */
export type RarityTable = Record<Rarity, number>;

export interface ZoneLootPool {
  /** Weapon and backpack tiers found here, with weights. */
  tiers: Partial<Record<1 | 2 | 3 | 4, number>>;
  /** Armor sets whose pieces drop here, with a weight per piece. */
  sets: Partial<Record<SetId, number>>;
  /** Accessory tiers found here (krug 5, F10), with a weight per item. */
  accessories?: Partial<Record<1 | 2 | 3 | 4, number>>;
  slotBias?: Partial<Record<GearSlot, number>>;
}

/** An accessory weighs half an armor piece in a zone's pool: they are rarer (F10.3). */
export const ACCESSORY_DROP_WEIGHT = 0.5;
const ACC = ACCESSORY_DROP_WEIGHT;

export const DROP_RATES = {
  /** Base rarity chances per zone (danger in the comment). */
  zones: {
    forest: { common: 72, uncommon: 24, rare: 3.7, epic: 0.3, legendary: 0 }, // 1
    gas_station: { common: 66, uncommon: 27, rare: 6, epic: 0.95, legendary: 0.05 }, // 2
    downtown: { common: 60, uncommon: 29, rare: 9, epic: 1.85, legendary: 0.15 }, // 3
    hospital: { common: 53, uncommon: 31, rare: 13, epic: 2.7, legendary: 0.3 }, // 4
    factory: { common: 53, uncommon: 31, rare: 13, epic: 2.7, legendary: 0.3 }, // 4
    military_base: { common: 46, uncommon: 32, rare: 17, epic: 4.4, legendary: 0.6 }, // 5
    mountains: { common: 53, uncommon: 31, rare: 13, epic: 2.7, legendary: 0.3 }, // 4
    wasteland: { common: 46, uncommon: 32, rare: 17, epic: 4.4, legendary: 0.6 }, // 5
    // Secret locations: better Rare and Epic odds, Legendary still under the 1% cap.
    secret_bunker: { common: 40, uncommon: 33, rare: 20, epic: 6.5, legendary: 0.5 }, // 4
    underground_lab: { common: 36, uncommon: 33, rare: 22, epic: 8.2, legendary: 0.8 }, // 5
    survivor_vault: { common: 30, uncommon: 33, rare: 26, epic: 10, legendary: 1 }, // 5
  } satisfies Record<ZoneId, RarityTable>,
  /** Rare, Epic and Legendary × (1 + perLevel × (level − 1)), at most `max`. */
  level: { perLevel: 0.015, max: 1.3 },
  /** Level and luck together never multiply by more than this. */
  maxMult: 1.5,
  /** Legendary chance per drop never goes above this (percent). */
  legendaryCap: 1,
  /** Bad-luck protection: each non-Legendary drop in danger 4+ adds `perDrop` percent. */
  pity: { minDanger: 4, perDrop: 0.002 },
} as const;

/**
 * What drops where (Item Catalog, "Gdje šta pada"): weapon and backpack tiers, and armor
 * sets with a weight each. Nightstalker shows up in Downtown "at night": half weight, around
 * the clock. Krug 5 (F10.3): Rider in the Gas Station and Downtown, Hunter in Downtown, the
 * Mountains (their first armor) and the Wasteland; accessories by tier (T1 Forest and Gas
 * Station, T2 Gas Station and Downtown, T3 Hospital and Factory, T4 Military Base, Mountains,
 * Wasteland and the secret zones), at half an armor piece's weight. 11. 10. 2026 (BACKLOG #36): the Foundry set drops where
 * the Foundry is, in Calder Steelworks, and in The Burn with every other set (as the Medic set in Mercy General); the new
 * weapons and packs drop by their tier like the rest.
 */
export const ZONE_LOOT: Record<ZoneId, ZoneLootPool> = {
  forest: { tiers: { 1: 1 }, sets: { drifter: 1 }, accessories: { 1: ACC } },
  gas_station: { tiers: { 1: 2, 2: 1 }, sets: { drifter: 1, worker: 1, rider: 1 }, accessories: { 1: ACC, 2: ACC } },
  downtown: { tiers: { 2: 1 }, sets: { scrapper: 1, nightstalker: 0.5, rider: 1, hunter: 1 }, accessories: { 2: ACC }, slotBias: { weapon: 2 } },
  hospital: { tiers: { 2: 1, 3: 2 }, sets: { medic: 1 }, accessories: { 3: ACC } },
  factory: { tiers: { 3: 1 }, sets: { worker: 1, scrapper: 1, foundry: 1 }, accessories: { 3: ACC } },
  military_base: { tiers: { 4: 1 }, sets: { military: 1, nightstalker: 1 }, accessories: { 4: ACC }, slotBias: { weapon: 2, body: 1.5, helmet: 1.3 } },
  mountains: { tiers: { 3: 1 }, sets: { hunter: 1 }, accessories: { 4: ACC } },
  wasteland: { tiers: { 4: 1 }, sets: { drifter: 1, worker: 1, scrapper: 1, hunter: 1, medic: 1, nightstalker: 1, military: 1, foundry: 1 }, accessories: { 4: ACC } },
  secret_bunker: { tiers: { 3: 1, 4: 2 }, sets: { military: 1, nightstalker: 1 }, accessories: { 4: ACC }, slotBias: { weapon: 2 } },
  underground_lab: { tiers: { 3: 1, 4: 2 }, sets: { nightstalker: 1, medic: 1 }, accessories: { 4: ACC } },
  survivor_vault: { tiers: { 4: 1 }, sets: { nightstalker: 1, military: 1 }, accessories: { 4: ACC } },
};

/** The player side of a drop roll. */
export interface DropPlayer {
  level: number;
  /** Luck from affixes and perks (0.1 = +10%). */
  luck?: number;
  /** Non-Legendary drops in danger 4+ since the last Legendary. */
  pity?: number;
  /** One-off boost for Rare+ on this roll (event loot, 0.5 = ×1.5), applied after the level/luck cap. */
  boost?: number;
  /** Lucky Denim (krug 4): chance that a drop comes one rarity higher (0.05 = 5%). */
  rarityUp?: number;
  /** Krug 5 (K16.1): drops from runs since the last Rare or better (the Rare bad-luck counter). */
  rarePity?: number;
}

/**
 * Krug 5 (K16.1): bad-luck protection for Rare. Every item a run finds that is not Rare or better adds
 * one; at `after` the next one is Rare (not better), and any Rare or better puts it back to 0. Only
 * drops from runs count (item finds, event loot, Lucky Find, the Skeleton Key's stash), never the
 * Trader, contracts, boxes, the horde or crafting. Mutable only for the simulations (RARE_PITY=18).
 */
export const RARE_PITY = { after: 15 };

export function nextRarePity(rarity: Rarity, pity: number): number {
  return RARITIES.indexOf(rarity) >= RARITIES.indexOf('rare') ? 0 : pity + 1;
}

export function baseRarityTable(zoneId: ZoneId): RarityTable {
  return DROP_RATES.zones[zoneId] ?? DROP_RATES.zones.forest;
}

export function levelMultiplier(level: number): number {
  return Math.min(DROP_RATES.level.max, 1 + DROP_RATES.level.perLevel * (Math.max(1, level) - 1));
}

/** Level × luck, capped. */
export function rarityMultiplier(p: DropPlayer): number {
  return Math.min(DROP_RATES.maxMult, levelMultiplier(p.level) * (1 + Math.max(0, p.luck ?? 0)));
}

function pityBonus(zoneId: ZoneId, pity: number): number {
  return ZONES[zoneId].danger >= DROP_RATES.pity.minDanger ? Math.max(0, pity) * DROP_RATES.pity.perDrop : 0;
}

/** Chances in percent for this player in this zone (they add up to 100). */
export function dropChances(zoneId: ZoneId, p: DropPlayer): RarityTable {
  const base = baseRarityTable(zoneId);
  const m = rarityMultiplier(p) * (1 + Math.max(0, p.boost ?? 0));
  const rare = base.rare * m;
  const epic = base.epic * m;
  // A zone without Legendary drops never gets one, whatever the modifiers.
  const legendary = base.legendary > 0 ? Math.min(DROP_RATES.legendaryCap, base.legendary * m + pityBonus(zoneId, p.pity ?? 0)) : 0;
  const uncommon = base.uncommon;
  const common = Math.max(0, 100 - (uncommon + rare + epic + legendary));
  return { common, uncommon, rare, epic, legendary };
}

/** Rolls the rarity of one drop. Takes any Rng so tests can inject a seeded one. */
export function rollRarity(zoneId: ZoneId, p: DropPlayer, rng: Rng): Rarity {
  const t = dropChances(zoneId, p);
  const total = RARITIES.reduce((a, r) => a + t[r], 0);
  let x = rng.next() * total;
  let last: Rarity = 'common';
  for (const r of RARITIES) {
    if (t[r] <= 0) continue;
    last = r;
    if (x < t[r]) return r;
    x -= t[r];
  }
  return last;
}

/** Bad-luck counter after a drop: reset by a Legendary, +1 for anything else in danger 4+. */
export function nextLegendaryPity(zoneId: ZoneId, rarity: Rarity, pity: number): number {
  if (rarity === 'legendary') return 0;
  return ZONES[zoneId].danger >= DROP_RATES.pity.minDanger ? pity + 1 : pity;
}

/** Best rarity that can drop in a zone at all (map pin). */
export function topRarity(zoneId: ZoneId): Rarity {
  const t = baseRarityTable(zoneId);
  return [...RARITIES].reverse().find((r) => t[r] > 0) ?? 'common';
}

/**
 * 11. 10. 2026 (owner, BACKLOG #36): the items of the item package share the slot share their slot had before them
 * (weapons, armor, packs, accessories): each group's weight is what it was without them, spread over old and new items
 * alike (a new item weighs as much as an old one of its group and tier), so the split of a pool between the slots stays
 * as it was. `keepSlotShare: false` (simulations): the first version, where the new items added their weight.
 */
export const DROP_POOL_RULES = { keepSlotShare: true };
const PACKAGE = new Set(ITEM_PACKAGE_IDS);

/** The slot group a template counts in for the slot shares: weapon, armor (a set piece), backpack or accessory. */
export const poolGroup = (t: Pick<ItemTemplate, 'slot' | 'setId'>): 'weapon' | 'armor' | 'backpack' | 'accessory' => (t.slot === 'weapon' ? 'weapon' : t.slot === 'backpack' ? 'backpack' : t.slot === 'accessory' ? 'accessory' : 'armor');

/** A pool's weights with each slot group scaled back to its weight without the item package (a group of only new items keeps its own). */
export function keepSlotShares<T extends Pick<ItemTemplate, 'id' | 'slot' | 'setId'>>(pool: [T, number][]): [T, number][] {
  if (!DROP_POOL_RULES.keepSlotShare) return pool;
  const now: Record<string, number> = {};
  const old: Record<string, number> = {};
  for (const [t, w] of pool) {
    const g = poolGroup(t);
    now[g] = (now[g] ?? 0) + w;
    if (!PACKAGE.has(t.id)) old[g] = (old[g] ?? 0) + w;
  }
  return pool.map(([t, w]) => {
    const g = poolGroup(t);
    const o = old[g] ?? 0;
    return [t, o > 0 && now[g] > 0 ? (w * o) / now[g] : w];
  });
}

/** Templates that can drop in a zone, with weights. */
export function zoneItemPool(zoneId: ZoneId): [ItemTemplate, number][] {
  const pool = ZONE_LOOT[zoneId] ?? ZONE_LOOT.forest;
  const out: [ItemTemplate, number][] = [];
  for (const t of ITEM_TEMPLATES) {
    if (isSlotLocked(t.slot)) continue;
    let w = 0;
    if (t.setId) w = pool.sets[t.setId] ?? 0;
    else if (t.slot === 'accessory') w = pool.accessories?.[t.tier] ?? 0;
    else w = pool.tiers[t.tier] ?? 0;
    if (!w) continue;
    w *= pool.slotBias?.[t.slot] ?? 1;
    out.push([t, w]);
  }
  return keepSlotShares(out);
}

export function pickZoneTemplate(zoneId: ZoneId, rng: Rng): ItemTemplate {
  return rng.weighted(zoneItemPool(zoneId));
}

/** Rarity table problems (rows must add up to 100, Legendary within the cap). */
export function validateDropRates(): string[] {
  const errors: string[] = [];
  for (const [zone, t] of Object.entries(DROP_RATES.zones) as [ZoneId, RarityTable][]) {
    const sum = RARITIES.reduce((a, r) => a + t[r], 0);
    if (Math.abs(sum - 100) > 1e-6) errors.push(`drop table ${zone} adds up to ${sum}, not 100`);
    if (t.legendary > DROP_RATES.legendaryCap) errors.push(`drop table ${zone}: Legendary ${t.legendary}% is over the ${DROP_RATES.legendaryCap}% cap`);
    if (!zoneItemPool(zone).length) errors.push(`zone ${zone} has no item pool`);
  }
  return errors;
}

/**
 * Lucky Denim: one rarity higher, never above the zone's best rarity, and an Epic only turns
 * Legendary while the Legendary chance stays under the 1% cap.
 */
export function rarityUpgrade(zoneId: ZoneId, rarity: Rarity, p: DropPlayer, rng: { next(): number }): Rarity {
  const i = RARITIES.indexOf(rarity);
  const next = RARITIES[i + 1];
  if (!next || RARITIES.indexOf(next) > RARITIES.indexOf(topRarity(zoneId))) return rarity;
  if (next === 'legendary') {
    const c = dropChances(zoneId, p);
    const extra = c.epic * (p.rarityUp ?? 0);
    const room = Math.max(0, DROP_RATES.legendaryCap - c.legendary);
    if (extra > room && rng.next() >= room / extra) return rarity;
  }
  return next;
}
